import { useState, useEffect, useCallback, useRef } from 'react';

export interface NowPlayingTrack {
  isPlaying: boolean;
  title: string;
  artist: string;
  album: string;
  albumArt: string | null;
  songUrl: string;
  /** Playback position in ms at the time the API responded, null when not playing */
  progress: number | null;
  /** Track duration in ms */
  duration: number;
  /** ISO timestamp of when it was last played (recent-tracks fallback only) */
  playedAt?: string;
  /** When the API snapshot was taken */
  asOf?: string;
  /** live | paused | history | cache */
  source?: string;
}

interface UseSpotifyNowPlayingReturn {
  track: NowPlayingTrack | null;
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;
  isConfigured: boolean;
  refresh: () => void;
}

const CACHE_KEY = 'jmos-spotify-last-track-v3';

function readCachedTrack(): NowPlayingTrack | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as NowPlayingTrack;
    if (!parsed?.title) return null;
    return parsed;
  } catch {
    return null;
  }
}

function normalizeTrack(data: NowPlayingTrack, source: 'api' | 'storage' = 'api'): NowPlayingTrack {
  if (source === 'storage') {
    return { ...data, isPlaying: false, progress: null };
  }
  const isLive = data.isPlaying === true && !data.playedAt;
  if (isLive) {
    return { ...data, isPlaying: true };
  }
  return { ...data, isPlaying: false, progress: null };
}

function writeCachedTrack(track: NowPlayingTrack) {
  try {
    if (track.isPlaying) return;

    const stored: NowPlayingTrack = {
      ...track,
      isPlaying: false,
      progress: null,
      source: 'cache',
    };
    if (!stored.playedAt) {
      stored.playedAt = stored.asOf || new Date().toISOString();
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(stored));
  } catch {
    // ignore quota / private mode
  }
}

export function useSpotifyNowPlaying(): UseSpotifyNowPlayingReturn {
  const configuredBaseUrl = process.env.REACT_APP_SPOTIFY_API_URL;
  const endpoint = process.env.NODE_ENV === 'production'
    ? '/api/now-playing'
    : configuredBaseUrl
    ? `${configuredBaseUrl.replace(/\/+$/, '')}/api/now-playing`
    : null;

  const [track, setTrack] = useState<NowPlayingTrack | null>(() => {
    const cached = readCachedTrack();
    return cached ? normalizeTrack(cached, 'storage') : null;
  });
  const [isLoading, setIsLoading] = useState(!!endpoint);
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const fetchCountRef = useRef(0);
  const trackRef = useRef<NowPlayingTrack | null>(track);

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  const fetchNowPlaying = useCallback(async () => {
    if (!endpoint) return;

    const fetchId = ++fetchCountRef.current;

    try {
      const res = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!res.ok) {
        const maybeJson = await res.json().catch(() => null);
        const code = maybeJson?.code;
        if (res.status === 503 || res.status === 429 || code === 'RATE_LIMITED') {
          const err = new Error('RATE_LIMITED');
          (err as Error & { transient?: boolean }).transient = true;
          throw err;
        }
        const message = maybeJson?.error || `HTTP ${res.status}`;
        throw new Error(code ? `${message} (${code})` : message);
      }

      const data: NowPlayingTrack | { error?: string; code?: string } | null = await res.json();

      if (fetchId !== fetchCountRef.current) return;

      if (data && typeof data === 'object' && 'title' in data && data.title) {
        const incoming = data as NowPlayingTrack;
        const current = trackRef.current;

        // Stale server cache must not replace a live session with a different track.
        if (
          incoming.source === 'cache'
          && current?.isPlaying === true
          && incoming.title !== current.title
        ) {
          return;
        }

        const normalized = normalizeTrack(incoming);
        writeCachedTrack(normalized);
        setTrack(normalized);
        setError(null);
        setIsEmpty(false);
      } else {
        const cached = readCachedTrack();
        setTrack(cached ? normalizeTrack(cached, 'storage') : null);
        const apiError = data && typeof data === 'object' && 'error' in data ? data.error : null;
        setError(typeof apiError === 'string' ? apiError : null);
        setIsEmpty(!cached);
      }
    } catch (err) {
      if (fetchId !== fetchCountRef.current) return;
      const cached = readCachedTrack();
      const transient = (err as Error & { transient?: boolean })?.transient === true;
      if (cached) {
        setTrack(normalizeTrack(cached, 'storage'));
        setError(null);
        setIsEmpty(false);
      } else if (transient) {
        setError(null);
        setIsEmpty(false);
      } else {
        setError(err instanceof Error ? err.message : 'Could not reach the Spotify proxy.');
        setIsEmpty(false);
      }
    } finally {
      if (fetchId === fetchCountRef.current) setIsLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (!endpoint) return;

    const pollMs = track?.isPlaying === true ? 15_000 : 60_000;
    fetchNowPlaying();
    const id = setInterval(fetchNowPlaying, pollMs);
    return () => clearInterval(id);
  }, [endpoint, fetchNowPlaying, track?.isPlaying]);

  return {
    track,
    isLoading,
    error,
    isEmpty,
    isConfigured: !!endpoint,
    refresh: fetchNowPlaying,
  };
}

/** Extrapolate live progress from the API snapshot timestamp. */
export function liveProgressMs(track: NowPlayingTrack | null, nowMs = Date.now()): number {
  if (!track) return 0;
  if (track.isPlaying !== true || track.progress == null) {
    return track.progress ?? 0;
  }
  if (!track.asOf) return track.progress;
  const elapsed = Math.max(0, nowMs - new Date(track.asOf).getTime());
  return Math.min(track.progress + elapsed, track.duration);
}
